/**
 * Mobile header hub expand/collapse — shared by homepage + collection pages.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');

  const initHub = function (root) {
    if (!root || root.dataset.mobileHubInitialized === 'true') return;
    root.dataset.mobileHubInitialized = 'true';

    const hubExpand = root.querySelector('[data-mobile-hub-expand]');
    const hubActions = root.querySelector('.editorial-homepage__actions');

    const resetMobileHubActionStyles = function () {
      if (!hubActions) return;

      hubActions.style.removeProperty('max-width');
      hubActions.style.removeProperty('margin-inline-start');
      hubActions.style.removeProperty('overflow');
      hubActions.style.removeProperty('opacity');
      hubActions.style.removeProperty('width');
      hubActions.style.removeProperty('transition');
      hubActions.style.removeProperty('gap');
    };

    const parseHubTimeMs = function (hubStyle, customProp, fallbackMs) {
      const raw = hubStyle.getPropertyValue(customProp).trim();
      if (!raw) return fallbackMs;

      const probe = document.createElement('div');
      probe.style.cssText =
        'position:fixed;left:-9999px;top:0;width:0;height:0;margin:0;padding:0;border:0;transition-duration:' +
        raw +
        ';';
      document.documentElement.appendChild(probe);
      const parsedMs = parseFloat(window.getComputedStyle(probe).transitionDuration) || 0;
      probe.remove();

      return parsedMs > 0 ? Math.round(parsedMs * 1000) : fallbackMs;
    };

    const getMobileHubActionGap = function (hubStyle) {
      return hubStyle.getPropertyValue('--editorial-mobile-hub-action-gap').trim() || '0.45rem';
    };

    const getMobileHubCloseSyncMs = function (hubStyle) {
      const durationSec = parseFloat(hubStyle.getPropertyValue('--editorial-mobile-hub-duration')) || 0.68;
      const staggerSec = parseFloat(hubStyle.getPropertyValue('--editorial-mobile-hub-stagger')) || 0.09;
      const fadeSec = parseFloat(hubStyle.getPropertyValue('--editorial-mobile-hub-fade')) || 0.5;

      return Math.round((staggerSec * 2 + Math.max(durationSec, fadeSec)) * 1000);
    };

    const getMobileHubExpandShiftOpenMs = function (hubStyle) {
      return parseHubTimeMs(hubStyle, '--editorial-mobile-hub-expand-shift-open', getMobileHubCloseSyncMs(hubStyle));
    };

    const getMobileHubExpandShiftCloseMs = function (hubStyle) {
      return parseHubTimeMs(
        hubStyle,
        '--editorial-mobile-hub-expand-shift-close',
        getMobileHubCloseSyncMs(hubStyle) + 260
      );
    };

    const pinMobileHubActionsOpen = function (hubStyle) {
      if (!hubActions) return;

      const trackOpen = hubStyle.getPropertyValue('--mobile-hub-track-open').trim() || '9rem';
      const toggleGap = hubStyle.getPropertyValue('--editorial-mobile-hub-toggle-gap').trim();

      hubActions.style.width = 'auto';
      hubActions.style.maxWidth = trackOpen;
      hubActions.style.marginInlineStart = toggleGap || '0px';
      hubActions.style.overflow = 'visible';
      hubActions.style.opacity = '1';
      hubActions.style.gap = getMobileHubActionGap(hubStyle);
    };

    const runMobileHubOpenLayout = function () {
      if (!hubActions) {
        return getMobileHubExpandShiftOpenMs(window.getComputedStyle(root));
      }

      const hubStyle = window.getComputedStyle(root);
      const openMs = getMobileHubExpandShiftOpenMs(hubStyle);
      const trackOpen = hubStyle.getPropertyValue('--mobile-hub-track-open').trim() || '9rem';
      const toggleGap = hubStyle.getPropertyValue('--editorial-mobile-hub-toggle-gap').trim();
      const actionGap = getMobileHubActionGap(hubStyle);
      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

      hubActions.style.transition = 'none';
      hubActions.style.width = 'auto';
      hubActions.style.maxWidth = '0px';
      hubActions.style.marginInlineStart = '0px';
      hubActions.style.gap = '0px';
      hubActions.style.opacity = '1';
      hubActions.style.overflow = 'hidden';

      void hubActions.offsetWidth;

      root.classList.add('is-mobile-hub-open');

      window.requestAnimationFrame(function () {
        hubActions.style.transition =
          'max-width ' +
          openMs +
          'ms ' +
          easing +
          ', margin-inline-start ' +
          openMs +
          'ms ' +
          easing +
          ', gap ' +
          openMs +
          'ms ' +
          easing;

        hubActions.style.maxWidth = trackOpen;
        hubActions.style.marginInlineStart = toggleGap || '0px';
        hubActions.style.gap = actionGap;
      });

      return openMs;
    };

    const runMobileHubCloseLayout = function () {
      if (!hubActions) {
        return getMobileHubExpandShiftCloseMs(window.getComputedStyle(root));
      }

      const hubStyle = window.getComputedStyle(root);
      const closeMs = getMobileHubExpandShiftCloseMs(hubStyle);
      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

      hubActions.style.opacity = '1';
      hubActions.style.overflow = 'visible';

      void hubActions.offsetWidth;

      window.requestAnimationFrame(function () {
        hubActions.style.transition =
          'max-width ' +
          closeMs +
          'ms ' +
          easing +
          ', margin-inline-start ' +
          closeMs +
          'ms ' +
          easing +
          ', gap ' +
          closeMs +
          'ms ' +
          easing;

        hubActions.style.maxWidth = '0px';
        hubActions.style.marginInlineStart = '0px';
        hubActions.style.gap = '0px';
      });

      return closeMs;
    };

    const clearMobileHubCloseTimer = function () {
      if (!root.dataset.hubCloseTimer) return;

      window.clearTimeout(Number(root.dataset.hubCloseTimer));
      root.dataset.hubCloseTimer = '';
    };

    const clearMobileHubTimers = function () {
      clearMobileHubCloseTimer();
      root.classList.remove('is-mobile-hub-pin-open');
    };

    const startMobileHubClose = function () {
      if (!hubActions) {
        root.classList.remove('is-mobile-hub-open');
        root.classList.add('is-mobile-hub-closing');

        const closeMs = getMobileHubCloseSyncMs(window.getComputedStyle(root)) + 80;
        const timerId = window.setTimeout(function () {
          root.classList.remove('is-mobile-hub-closing');
          root.dataset.hubCloseTimer = '';
        }, closeMs);

        root.dataset.hubCloseTimer = String(timerId);
        return;
      }

      const hubStyle = window.getComputedStyle(root);

      hubActions.style.transition = 'none';
      pinMobileHubActionsOpen(hubStyle);
      root.classList.add('is-mobile-hub-pin-open');

      void hubActions.offsetWidth;

      root.classList.remove('is-mobile-hub-open');
      root.classList.add('is-mobile-hub-closing');

      const closeSyncMs = runMobileHubCloseLayout();
      const closeMs = closeSyncMs + 80;
      const timerId = window.setTimeout(function () {
        root.classList.remove('is-mobile-hub-closing', 'is-mobile-hub-pin-open');
        resetMobileHubActionStyles();
        root.dataset.hubCloseTimer = '';
      }, closeMs);

      root.dataset.hubCloseTimer = String(timerId);
    };

    const setMobileHubOpen = function (isOpen) {
      if (!MOBILE_MQ.matches) {
        clearMobileHubTimers();
        resetMobileHubActionStyles();
        root.classList.remove('is-mobile-hub-open', 'is-mobile-hub-closing', 'is-mobile-hub-pin-open');
        if (hubExpand) hubExpand.setAttribute('aria-expanded', 'false');
        return;
      }

      clearMobileHubTimers();

      if (isOpen) {
        resetMobileHubActionStyles();
        root.classList.remove('is-mobile-hub-closing', 'is-mobile-hub-pin-open');

        const skipOpenAnim = root.dataset.mobileHubInitOpen === 'true';
        if (skipOpenAnim) {
          root.dataset.mobileHubInitOpen = '';
          root.classList.add('is-mobile-hub-open');
          pinMobileHubActionsOpen(window.getComputedStyle(root));
        } else if (!root.classList.contains('is-mobile-hub-open')) {
          runMobileHubOpenLayout();
        }
      } else if (root.classList.contains('is-mobile-hub-open')) {
        startMobileHubClose();
      } else {
        resetMobileHubActionStyles();
        root.classList.remove('is-mobile-hub-open', 'is-mobile-hub-closing', 'is-mobile-hub-pin-open');
      }

      const ariaExpanded = isOpen || root.classList.contains('is-mobile-hub-open');

      if (hubExpand) hubExpand.setAttribute('aria-expanded', ariaExpanded ? 'true' : 'false');

      root.dispatchEvent(
        new CustomEvent('editorial:mobile-hub-toggle', {
          bubbles: true,
          detail: { open: Boolean(ariaExpanded) },
        })
      );
    };

    const toggleMobileHub = function (event) {
      if (!MOBILE_MQ.matches) return;

      event.preventDefault();
      event.stopPropagation();

      if (root.classList.contains('is-mobile-hub-closing') || root.classList.contains('is-mobile-hub-pin-open')) {
        setMobileHubOpen(true);
        return;
      }

      setMobileHubOpen(!root.classList.contains('is-mobile-hub-open'));
    };

    if (hubExpand) hubExpand.addEventListener('click', toggleMobileHub);

    if (MOBILE_MQ.matches) {
      root.dataset.mobileHubInitOpen = 'true';
      setMobileHubOpen(true);
    }

    const onMqChange = function () {
      setMobileHubOpen(MOBILE_MQ.matches);
    };

    if (typeof MOBILE_MQ.addEventListener === 'function') {
      MOBILE_MQ.addEventListener('change', onMqChange);
    } else if (typeof MOBILE_MQ.addListener === 'function') {
      MOBILE_MQ.addListener(onMqChange);
    }
  };

  const boot = function () {
    document.querySelectorAll('[data-editorial-mobile-hub]').forEach(initHub);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
